import { useEffect, useState } from "react";
import { CaretDown, X } from "@phosphor-icons/react";
import { Layers, PencilRuler } from "lucide-react";
import { AppButton, AppInput, FigmaTypography, RadioCard, isEnterSubmit } from "@libs/zapi-ui/custom";
import { Dialog, DialogContent, DialogTitle, Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { formatPortfolioDate } from "../../lib/formatPortfolioDate";
import type { TVersionHistoryItem } from "./VersionHistoryPanel";
import { useTranslation } from "react-i18next";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

type TVersionType = "draft" | "full";

type TProps = {
  open: boolean;
  projectName?: string;
  projectLogoUrl?: string | null;
  versions: TVersionHistoryItem[];
  defaultSourceId: string;
  isLoading: boolean;
  onCancel: () => void;
  onCreate: (sourceId: string, name: string, isDraft: boolean) => void;
};

// The name is stored in English because it is parsed back below to work out the next ordinal;
// the dialog shows it translated and saves the English form when it is left unchanged.
function nextNewVersionName(versions: TVersionHistoryItem[]): string {
  let maxOrdinal = 0;
  for (const v of versions) {
    if (v.name === "New Version") {
      maxOrdinal = Math.max(maxOrdinal, 1);
      continue;
    }
    const match = v.name.match(/^New Version (\d+)$/);
    if (match) maxOrdinal = Math.max(maxOrdinal, parseInt(match[1], 10));
  }
  return maxOrdinal === 0 ? "New Version" : `New Version ${maxOrdinal + 1}`;
}

function FieldLabel({ children }: { children: string }) {
  return (
    <FigmaTypography component="span" variant="label-2" color="zinc900" className="flex h-[10px] items-center">
      {children}
    </FigmaTypography>
  );
}

function VersionOptionRow({ version, onSelect }: { version: TVersionHistoryItem; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex h-[58px] w-full items-center gap-2 rounded-md px-2 text-left hover:bg-innoveraZinc-50"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1 pt-1.5">
        <span className="flex h-[10px] min-w-0 items-center gap-2">
          <span
            className={cn(
              "size-1.5 shrink-0 rounded-full",
              version.isDraft ? "bg-innoveraYellow-400" : "bg-innoveraGreen-600"
            )}
          />
          <FigmaTypography component="span" variant="label-2" color="zinc900" className="min-w-0 truncate">
            {displayVersionName(version.name)}
          </FigmaTypography>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <FigmaTypography component="span" variant="body-2" color="zinc500" className="shrink-0 tabular-nums">
            {formatPortfolioDate(version.date)}
          </FigmaTypography>
          {version.modifiedBy ? (
            <>
              <span className="size-0.5 shrink-0 rounded-full bg-innoveraZinc-400" />
              <FigmaTypography component="span" variant="body-2" color="zinc500" className="min-w-0 truncate">
                {version.modifiedBy}
              </FigmaTypography>
            </>
          ) : null}
        </span>
      </span>
    </button>
  );
}

export function NewVersionModalV2(props: TProps) {
  const { open, projectName, projectLogoUrl, versions, defaultSourceId, isLoading, onCancel, onCreate } = props;

  const { t } = useTranslation("common");
  const defaultName = nextNewVersionName(versions);
  const [name, setName] = useState(() => displayVersionName(defaultName));
  const [type, setType] = useState<TVersionType>("draft");
  const [sourceId, setSourceId] = useState(defaultSourceId);
  const [sourceOpen, setSourceOpen] = useState(false);

  const isFullAnalysis = type === "full";

  useEffect(() => {
    if (!isFullAnalysis) return;
    const selected = versions.find((v) => v.id === sourceId);
    if (selected?.isDraft) {
      const firstNonDraft = versions.find((v) => !v.isDraft);
      if (firstNonDraft) setSourceId(firstNonDraft.id);
    }
  }, [isFullAnalysis, sourceId, versions]);

  const selectableVersions = isFullAnalysis ? versions.filter((v) => !v.isDraft) : versions;
  const selected = versions.find((v) => v.id === sourceId);
  const submit = () => {
    const trimmed = name.trim();
    onCreate(sourceId, trimmed === displayVersionName(defaultName) ? defaultName : trimmed, type === "draft");
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent
        data-testid="new-version-modal"
        showCloseButton={false}
        aria-describedby={undefined}
        className="w-[480px] max-w-[480px] grid-cols-[minmax(0,1fr)] gap-0 overflow-hidden rounded-xl border-0 p-0 shadow-300 ring-1 ring-inset ring-innoveraZinc-150"
      >
        <div className="flex h-[92px] shrink-0 items-center gap-4 p-5">
          <span className="flex size-[52px] shrink-0 items-center justify-center overflow-hidden rounded-lg bg-innoveraZinc-100">
            {projectLogoUrl ? (
              <img src={projectLogoUrl} alt="" className="size-full object-contain p-2.5" />
            ) : (
              <InnoveraIcon className="size-6 text-innoveraZinc-400" />
            )}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <DialogTitle asChild>
              <FigmaTypography component="span" variant="heading-3" color="zinc900" className="truncate leading-6">
                {projectName ?? t("versions.untitledProject")}
              </FigmaTypography>
            </DialogTitle>
            <FigmaTypography component="span" variant="body-2" color="zinc500">
              {t("versions.newTitle")}
            </FigmaTypography>
          </span>
          <button
            type="button"
            aria-label={t("versions.close")}
            onClick={onCancel}
            className="flex size-5 shrink-0 items-center justify-center text-innoveraZinc-500 hover:text-innoveraZinc-900"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5">
          <div className="flex flex-col gap-2.5">
            <FieldLabel>{t("versions.nameLabel")}</FieldLabel>
            <AppInput
              value={name}
              placeholder={t("versions.namePlaceholder")}
              disabled={isLoading}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (isEnterSubmit(e, { allowShift: true })) {
                  e.preventDefault();
                  submit();
                }
              }}
              className="h-9 text-[14px]"
            />
          </div>

          <div className="flex flex-col gap-2.5">
            <FieldLabel>{t("versions.baseOn")}</FieldLabel>
            <Popover open={sourceOpen} onOpenChange={setSourceOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={isLoading}
                  data-testid="base-on-trigger"
                  className="flex h-[38px] w-full items-center justify-between gap-2 rounded-lg bg-white px-3 ring-1 ring-inset ring-innoveraZinc-150 disabled:opacity-50"
                >
                  <FigmaTypography component="span" variant="body-2" color="zinc900" className="min-w-0 truncate">
                    {displayVersionName(selected?.name)}
                  </FigmaTypography>
                  <CaretDown size={16} className="shrink-0 text-innoveraZinc-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={4}
                className="w-[var(--radix-popover-trigger-width)] space-y-1 rounded-lg border-0 p-1 shadow-300 ring-1 ring-inset ring-innoveraZinc-150"
              >
                {selectableVersions.map((v) => (
                  <VersionOptionRow
                    key={v.id}
                    version={v}
                    onSelect={() => {
                      setSourceId(v.id);
                      setSourceOpen(false);
                    }}
                  />
                ))}
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col gap-2.5">
            <FieldLabel>{t("versions.scope")}</FieldLabel>
            <div className="flex flex-col gap-1.5">
              <RadioCard
                selected={type === "full"}
                disabled={isLoading}
                onClick={() => setType("full")}
                icon={<Layers />}
                title={t("versions.fullVersion")}
                description={t("versions.fullVersionDescription")}
              />
              <RadioCard
                selected={type === "draft"}
                disabled={isLoading}
                onClick={() => setType("draft")}
                icon={<PencilRuler />}
                title={t("versions.frameOnly")}
                description={t("versions.frameOnlyDescription")}
              />
            </div>
          </div>
        </div>

        <div className="flex h-[72px] shrink-0 items-center justify-end gap-2 p-5">
          <AppButton type="button" appearance="outlined" color="zinc150" disabled={isLoading} onClick={onCancel}>
            {t("cancel")}
          </AppButton>
          <AppButton type="button" color="zinc950" isLoading={isLoading} onClick={submit}>
            {t("versions.create")}
          </AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
